import { BatchActionEnum, TableTypeEnum } from '@/sdk/model/TableDefinitionInputV1';
import { SearchTypeEnum } from '@/sdk/model/PropertySearchV1';
import {
  ItemTableCell,
  MaterializedTableOutput,
  ProcessedMaterializedTable,
  RowLoadProgress,
  ScalingTableColumnDefinition,
  TreeHierarchy,
} from '@/tableDefinitionEditor/tableDefinition.types';
import {
  ItemSearchPreviewV1,
  RowRulesResultV1,
  RulesResultV1,
  TableDefinitionInputV1,
  TableDefinitionOutputV1,
} from '@/sdk';
import { GridApi } from 'ag-grid-community';
import { PersistenceLevel, Store } from '@/core/flux.service';
import {
  DATUM_ID_COLUMN,
  DEFAULT_COLUMN_DEFINITION_INPUTS,
  DEFAULT_PROPERTIES_FOR_ALL_ITEM_COLUMNS,
  ID_COLUMN,
} from '@/tableDefinitionEditor/tableDefinition.constants';
import {
  columnDefinitionOutputToColumnDefinitionInput,
  generateTableName,
  tableDefinitionOutputToTableDefinitionInput,
} from '@/tableDefinitionEditor/tableDefinition.utilities';
import { ColumnTypeEnum } from '@/sdk/model/ColumnDefinitionInputV1';
import { SeeqNames } from '@/main/app.constants.seeqnames';
import { transpose } from '@/utilities/utilities';
import { createSelector } from 'reselect';
import { t } from 'i18next';
import { columnRuleOutputToColumnRuleInput } from '@/tableDefinitionEditor/columnRules/columnRule.utilities';
import { ColumnRule } from '@/tableDefinitionEditor/columnRules/columnRule.constants';
import { processMaterializedTable } from '@/tableDefinitionEditor/processedMaterializedTable.utilities';

/**
 * Base class store for the table definition
 * The table definition state is stored here
 * which is updated when we receive messages from the server on the socket for table definitions.
 * This base class contains all handler functions, getters and state management that can be used in other editor stores
 */
export abstract class TableDefinitionStore extends Store {
  persistenceLevel: PersistenceLevel = 'NONE';
  abstract defaultName: string;
  setupState(defaultType: TableTypeEnum) {
    this.state = this.immutable({
      id: '',
      subscriberId: '',
      name: '',
      searchTypeAI: undefined,
      description: '',
      scopedTo: undefined,
      batchAction: BatchActionEnum.UPDATEEXISTINGINSERTNEWCLEANUP,
      tableType: defaultType,
      columns: [],
      hasUnsupportedSubscription: undefined,
      shouldSearchForItems: false,
      tableDefinitionInput: this.monkey(
        ['subscriberId'],
        ['name'],
        ['description'],
        ['scopedTo'],
        ['batchAction'],
        ['tableType'],
        ['columns'],
        function (
          subscriberId: string,
          name: string,
          description: string,
          scopedTo: string | undefined,
          batchAction: BatchActionEnum,
          tableType: TableTypeEnum,
          columnDefinitions: ScalingTableColumnDefinition[],
        ): TableDefinitionInputV1 {
          const columnDefinitionInputs =
            columnDefinitions.length === 0
              ? DEFAULT_COLUMN_DEFINITION_INPUTS
              : columnDefinitions.map((column) =>
                  columnDefinitionOutputToColumnDefinitionInput(column, columnDefinitions),
                );

          return {
            subscriptionId: subscriberId,
            name,
            description,
            scopedTo,
            batchAction,
            tableType,
            columnDefinitions: columnDefinitionInputs,
          };
        },
      ),
      tableDefinitionOutput: undefined,
      materializedTable: undefined,
      doTableReload: false,
      itemColumnsThatCouldBeUsedToAutoCreateTextColumns: this.monkey(
        ['columns'],
        (columns: ScalingTableColumnDefinition[]) => {
          return (
            columns
              .map((column, index) => ({ ...column, columnIndex: index + 1 }))
              .filter((column) => column.columnType === ColumnTypeEnum.UUID && column.displayProperty)
              // filter out an item column if there is a text column whose first rule is item property and has the same property
              // as the item column is currently displaying
              .filter((itemColumnWithProperty) => {
                const textColumnWithItemPropertyRule = columns.find((col) => {
                  const getItemPropertyRule =
                    col.columnType === ColumnTypeEnum.TEXT &&
                    col.rules.length > 0 &&
                    col.rules[0].rule === SeeqNames.MaterializedTables.Rules.GetItemProperty
                      ? columnRuleOutputToColumnRuleInput(
                          col.rules[0],
                          columns.filter((columnDef) => columnDef.columnName !== col.columnName),
                        )
                      : undefined;
                  const isPropertyMatch =
                    getItemPropertyRule?.getItemProperty?.propertyName === itemColumnWithProperty.displayProperty;
                  const inputColumnOfRuleIsItemColumn =
                    getItemPropertyRule?.getItemProperty?.columnIndex === itemColumnWithProperty.columnIndex;
                  return isPropertyMatch && inputColumnOfRuleIsItemColumn ? col : undefined;
                });
                return !textColumnWithItemPropertyRule;
              })
              .map((column) => ({
                columnName: column.columnName,
                displayProperty: column.displayProperty,
                displayName: column.displayName,
              })) as Required<Pick<ScalingTableColumnDefinition, 'columnName' | 'displayName' | 'displayProperty'>>[]
          );
        },
      ),
      pendingTableIds: {},
      showEvaluationDetailsModal: false,
      evaluationDetailsColumn: {},
      createdItemFinderId: undefined,
      allowHiddenColumnsAsInputs: false,
      agGridApi: undefined,
      rowLoadProgress: undefined,
      rowDataIsLoading: false,
      selectedColumns: [],
      columnOptions: undefined,
      scrollToColumnId: '',
    });
  }

  getName: (state: { tableDefinitions: ItemSearchPreviewV1[]; currentName: string; id: string }) => string =
    createSelector(
      (state: { tableDefinitions: ItemSearchPreviewV1[]; currentName: string; id: string }) => state.tableDefinitions,
      (state: { tableDefinitions: ItemSearchPreviewV1[]; currentName: string; id: string }) => state.id,
      (state: { tableDefinitions: ItemSearchPreviewV1[]; currentName: string; id: string }) => state.currentName,
      (tableDefinitions: ItemSearchPreviewV1[], id: string, currentName?: string) => {
        return generateTableName(id, tableDefinitions, this.defaultName, currentName);
      },
    );

  abstract getRulesNotAllowed(): ColumnRule[];

  abstract getMaterializedTableCancellationGroup(): string;
  abstract getTableDefinitionCancellationGroup(): string;

  get id(): string {
    return this.state.get('id');
  }

  get shouldSearchForItems(): boolean {
    return this.state.get('shouldSearchForItems');
  }

  get pendingTableIds(): Record<string, boolean> {
    return this.state.get('pendingTableIds');
  }

  get subscriberId(): string {
    return this.state.get('subscriberId');
  }

  get createdItemFinderId(): string {
    return this.state.get('createdItemFinderId');
  }

  abstract get name(): string;

  get tableType(): TableTypeEnum {
    return this.state.get('tableType');
  }

  get searchTypeAI(): SearchTypeEnum | undefined {
    return this.state.get('searchTypeAI');
  }

  get description(): string {
    return this.state.get('description');
  }

  get scopedTo(): string | undefined {
    return this.state.get('scopedTo');
  }

  get batchAction(): BatchActionEnum {
    return this.state.get('batchAction');
  }

  get columns(): ScalingTableColumnDefinition[] {
    return this.state.get('columns');
  }

  get tableDefinitionInput(): TableDefinitionInputV1 {
    return this.state.get('tableDefinitionInput');
  }

  get tableDefinitionOutput(): TableDefinitionOutputV1 {
    return this.state.get('tableDefinitionOutput');
  }

  get materializedTable(): ProcessedMaterializedTable | undefined {
    return this.state.get('materializedTable');
  }

  get showEvaluationDetailsModal(): boolean {
    return this.state.get('showEvaluationDetailsModal');
  }

  get evaluationDetailsColumn(): { evaluationColumnDef: ScalingTableColumnDefinition; rulesResult: RulesResultV1[] } {
    return this.state.get('evaluationDetailsColumn');
  }

  get itemColumnsThatCouldBeUsedToAutoCreateTextColumns(): Required<
    Pick<ScalingTableColumnDefinition, 'columnName' | 'displayName' | 'displayProperty'>
  >[] {
    return this.state.get('itemColumnsThatCouldBeUsedToAutoCreateTextColumns');
  }

  get hasUnsupportedSubscription(): boolean | undefined {
    return this.state.get('hasUnsupportedSubscription');
  }

  get doTableReload(): boolean {
    return this.state.get('doTableReload');
  }

  get allowHiddenColumnsAsInputs(): boolean {
    return this.state.get('allowHiddenColumnsAsInputs');
  }

  get agGridApi(): GridApi | undefined {
    return this.state.get('agGridApi');
  }

  get selectedColumns(): string[] {
    return this.state.get('selectedColumns');
  }

  get showConfigureColumn(): boolean {
    return this.state.get('showConfigureColumn');
  }

  get columnOptions(): TreeHierarchy | undefined {
    return this.state.get('columnOptions');
  }

  get scrollToColumnId(): string {
    return this.state.get('scrollToColumnId');
  }

  getColumnDisplayName = (columnName: string): string => {
    if (columnName === DATUM_ID_COLUMN.columnName) {
      return this.tableDefinitionOutput.uniqueIDColumnName ?? t('SCALING.DEFAULT_DATUM_ID_COLUMN_NAME');
    }

    if (columnName === SeeqNames.MaterializedTables.CsvItemIdColumn) {
      return t('SCALING.DEFAULT_ITEM_ID_COLUMN_NAME');
    }

    if (columnName === ID_COLUMN.columnName) {
      return t('SCALING.DEFAULT_ITEM_ID_COLUMN_NAME');
    }

    return columnName;
  };

  getColumnFromProcessedMaterializedTable = (columnName: string): ItemTableCell[] => {
    const materializedTable = this.materializedTable;
    if (!materializedTable) {
      return [];
    }
    const rows = materializedTable.rows;
    const rowsAsColumns = transpose(rows);
    const columnIndex = materializedTable.headers.findIndex((header) => header.name === columnName);
    return rowsAsColumns[columnIndex] as unknown as ItemTableCell[];
  };

  get rowLoadProgress(): RowLoadProgress | undefined {
    return this.state.get('rowLoadProgress');
  }

  get rowDataIsLoading(): boolean {
    return this.state.get('rowDataIsLoading');
  }

  protected readonly handlers = {};

  setSubscriberId = ({ subscriberId }: { subscriberId: string | undefined }) => {
    this.state.set('subscriberId', subscriberId);
  };

  setShouldSearchForItems = ({ shouldSearchForItems }: { shouldSearchForItems: boolean }) => {
    this.state.set('shouldSearchForItems', shouldSearchForItems);
  };

  setName = ({ name }: { name: string }) => {
    this.state.set('name', name);
  };

  setSearchTypeAI = ({ searchTypeAI }: { searchTypeAI: SearchTypeEnum }) => {
    this.state.set('searchTypeAI', searchTypeAI);
  };

  setDescription = ({ description }: { description: string }) => {
    this.state.set('description', description);
  };

  setBatchAction = ({ batchAction }: { batchAction: BatchActionEnum }) => {
    this.state.set('batchAction', batchAction);
  };

  setDoTableReload = ({ doTableReload }: { doTableReload: boolean }) => {
    this.state.set('doTableReload', doTableReload);
  };

  setTableDefinition = ({ tableDefinition }: { tableDefinition: TableDefinitionOutputV1 }) => {
    this.state.set('tableDefinitionOutput', tableDefinition);

    this.state.set('id', tableDefinition.id);
    this.state.set('name', tableDefinition.name);

    const hasUnsupportedSubscription = tableDefinition.subscription?.type === SeeqNames.Types.ConditionMonitor;
    this.state.set('hasUnsupportedSubscription', hasUnsupportedSubscription);
    if (hasUnsupportedSubscription) {
      return;
    }

    const tableDefinitionInput = tableDefinitionOutputToTableDefinitionInput(tableDefinition, this.scopedTo);
    this.state.set('subscriberId', tableDefinitionInput.subscriptionId);
    this.state.set('description', tableDefinitionInput.description);
    this.state.set('scopedTo', tableDefinitionInput.scopedTo);
    this.state.set('batchAction', tableDefinitionInput.batchAction);
    this.state.set('tableType', tableDefinitionInput.tableType);
    const currentColumns: ScalingTableColumnDefinition[] = this.state.get('columns');
    const newColumnDefinitions = tableDefinition.columnDefinitions;
    const updatedColumns: ScalingTableColumnDefinition[] = newColumnDefinitions.map((newColumn) => {
      const existingColumn = currentColumns.find((column) => column.columnName === newColumn.columnName);
      const displayName = this.getColumnDisplayName(newColumn.columnName);
      const displayProperty = newColumn.columnType === ColumnTypeEnum.UUID ? newColumn?.displayProperty : null;
      const isUUIDColumn = newColumn.columnType === ColumnTypeEnum.UUID;
      const hideColumnAsInput = newColumn.rules.some(
        (rule) => rule.rule === SeeqNames.MaterializedTables.Rules.SetItemProperty,
      );
      const additionalProperties = isUUIDColumn
        ? existingColumn?.additionalProperties ?? DEFAULT_PROPERTIES_FOR_ALL_ITEM_COLUMNS
        : undefined;
      const isHiddenFromDropdown = hideColumnAsInput || existingColumn?.isHiddenFromDropdown;
      return {
        displayName,
        ...(displayProperty !== null && { displayProperty }),
        additionalProperties,
        isHiddenFromDropdown,
        ...newColumn,
      };
    });
    this.state.set('columns', updatedColumns);
  };

  setMaterializedTable = ({ materializedTable }: { materializedTable: MaterializedTableOutput }) => {
    const columns: ScalingTableColumnDefinition[] = this.state.get('columns');
    const processedMaterializedTable = processMaterializedTable(materializedTable, columns);
    this.state.set('materializedTable', processedMaterializedTable);
  };

  setEvaluationResults = ({ evaluationResults }: { evaluationResults: { [key: string]: RowRulesResultV1[] } }) => {
    const columns: ScalingTableColumnDefinition[] = this.state.get('columns');

    const updatedColumns: ScalingTableColumnDefinition[] = columns.map((newColumn) => {
      return {
        evaluationDetails: evaluationResults[newColumn.id.toLowerCase()],
        ...newColumn,
      };
    });
    this.state.set('columns', updatedColumns);
  };

  reset = () => {
    this.initialize();
  };

  addPendingTableId = ({ pendingTableId }: { pendingTableId: string }) => {
    this.state.set(['pendingTableIds', pendingTableId], true);
  };

  removePendingTableId = ({ pendingTableId }: { pendingTableId: string }) => {
    this.state.unset(['pendingTableIds', pendingTableId]);
  };

  setShowEvaluationDetailsModal = ({ show }: { show: boolean }) => {
    this.state.set('showEvaluationDetailsModal', show);
  };

  setEvaluationDetailsColumn = ({
    evaluationColumnDef,
    rulesResult,
  }: {
    evaluationColumnDef: ScalingTableColumnDefinition;
    rulesResult: RulesResultV1[];
  }) => {
    this.state.set('evaluationDetailsColumn', { evaluationColumnDef, rulesResult });
  };

  setCreatedItemFinderId = ({ createdItemFinderId }: { createdItemFinderId: string | undefined }) => {
    this.state.set('createdItemFinderId', createdItemFinderId);
  };

  setAllowHiddenColumnsAsInputs = ({ allowHiddenColumnsAsInputs }: { allowHiddenColumnsAsInputs: boolean }) => {
    this.state.set('allowHiddenColumnsAsInputs', allowHiddenColumnsAsInputs);
  };

  setAgGridApi = ({ agGridApi }: { agGridApi: GridApi }) => {
    this.state.set('agGridApi', agGridApi);
  };

  setRowDataIsLoading = ({ rowDataIsLoading }: { rowDataIsLoading: boolean }) => {
    this.state.set('rowDataIsLoading', rowDataIsLoading);
  };

  requestSetProgress = (payload: {
    id: string;
    progress: Record<string, unknown>;
    timingInformation?: Record<string, unknown>;
    meterInformation?: Record<string, unknown>;
  }) => {
    if (payload.id === this.id) {
      this.state.set('rowLoadProgress', {
        progress: payload.progress ?? 0,
        timingInformation: payload.timingInformation,
        meterInformation: payload.meterInformation,
      });
    }
  };

  setSelectedColumns = ({ selectedColumns }: { selectedColumns: string[] }) => {
    this.state.set('selectedColumns', selectedColumns);
  };

  setShowConfigureColumn = ({ show }: { show: boolean }) => {
    this.state.set('showConfigureColumn', show);
  };

  setColumnOptions = ({ columnOptions }: { columnOptions: TreeHierarchy | undefined }) => {
    this.state.set('columnOptions', columnOptions);
  };

  setScrollToColumnId = ({ scrollToColumnId }: { scrollToColumnId: string }) => {
    this.state.set('scrollToColumnId', scrollToColumnId);
  };
}
